import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeT } from "../locales/importCopy";
import DigitalMixHint from "./DigitalMixHint";
import * as XLSX from "xlsx";
import { supabase } from "../lib/supabaseClient";
import { inferMediumCategory } from "../utils/mediumCategory";
import ImportJobsList from "./ImportJobsList";
import ImportFilesByInventory from "./ImportFilesByInventory";
import { secretLevel } from "../utils/secretGuard";
import {
  normalizeHeritageStatus, parseDimensions, cleanValue, toNumber, excelDateToISO,
  suggestMapping, analyzeRows, reportToCsv, entriesFromAnalysis,
  normalizeCurrency, currencyFromAmount, normalizeAvailability,
} from "../utils/importHelpers";
import { downloadImportTemplate } from "../utils/importTemplate";
import { withoutProposalWorks, applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

// ─── Champs cibles ────────────────────────────────────────────────────────────
const TARGET_FIELDS = [
  "title", "artist", "year", "medium", "description", "dimensions", "tags", "typeOfArt",
  "estimated_value", "currency", "value", "sale_price",
  "inventory_number",
  "provenance",
  "acquisition_date", "acquisition_price", "acquisition_type", "acquisition_source",
  "last_estimation_date",
  "availability", "condition", "conditionNotes",
  "isEdition", "editionInfo", "signature", "signature_desc",
  "location_name", // texte libre → résolu en UUID avant envoi
  // Patrimoine (galeries, archives, estates) et art numérique
  "heritage_status",
  "external_source", "external_id",
  "source_url", "source_updated_at", "source_collection",
  "digital_format", "digital_usage_rights", "digital_resale_conditions", "digital_transmission_conditions",
  "digital_display_equipment", "digital_access_custodian", "digital_access_procedure", "digital_heir_instructions",
];

// Colonnes « digital_* » → clés de artworks.digital_asset (JSON)
const DIGITAL_COLUMNS = {
  digital_format: "format",
  digital_usage_rights: "usage_rights",
  digital_resale_conditions: "resale_conditions",
  digital_transmission_conditions: "transmission_conditions",
  digital_display_equipment: "display_equipment",
  digital_access_custodian: "access_custodian",
  digital_access_procedure: "access_procedure",
  digital_heir_instructions: "heir_instructions",
};

// Champs mis à jour quand une œuvre est déjà connue par sa référence externe (synchronisation ponctuelle).
const EXTERNAL_UPDATABLE = [
  "title", "artist", "year", "medium", "description", "condition", "conditionNotes", "typeOfArt", "availability",
  "estimated_value", "value", "currency", "acquisition_date", "acquisition_price", "acquisition_type",
  "acquisition_source", "isEdition", "editionInfo", "heritage_status",
  "source_url", "source_updated_at", "source_collection", "imported_at",
];

const FIELD_LABELS = {
  title: "Titre de l'œuvre",
  artist: "Artiste (nom complet)",
  year: "Année",
  medium: "Technique / medium",
  description: "Description",
  dimensions: "Dimensions",
  tags: "Mots-clés (séparés par des virgules)",
  typeOfArt: "Type d'œuvre",
  estimated_value: "Valeur estimée",
  currency: "Devise (EUR, USD…)",
  value: "Valeur numérique (optionnel)",
  price: "Prix de référence historique (ex: prix d'achat initial)",
  sale_price: "Prix de vente",
  inventory_number: "Numéro d'inventaire",
  provenance: "Provenance (galerie, maison de vente…)",
  acquisition_date: "Date d'acquisition",
  acquisition_price: "Prix d'acquisition",
  acquisition_type: "Type d'acquisition (achat, héritage, donation…)",
  acquisition_source: "Source (galerie, maison de vente, particulier…)",
  last_estimation_date: "Date de dernière estimation",
  availability: "Disponibilité (En collection, En vente…)",
  is_public_for_sale: "Mettre en vente sur la marketplace Artkiv",
  condition: "État de conservation",
  conditionNotes: "Commentaires sur l'état",
  isEdition: "Œuvre en édition ? (true/false)",
  editionInfo: "Infos d'édition (ex: 2/8 + 2EA)",
  signature: "Signée ? (true/false)",
  signature_desc: "Détails sur la signature",
  location_name: "Lieu de stockage",
  heritage_status: "Statut patrimonial (stock marchand, propriété personnelle, société, dépôt…)",
  external_source: "Plateforme ou outil d'origine (ex. sedition)",
  external_id: "Identifiant de l'œuvre chez la plateforme d'origine",
  source_url: "Adresse de la fiche de l'œuvre sur la plateforme (lien http/https)",
  source_updated_at: "Date de dernière mise à jour côté plateforme",
  source_collection: "Collection thématique de la plateforme",
  digital_format: "Œuvre numérique : média et format",
  digital_usage_rights: "Œuvre numérique : droits d'usage et d'affichage",
  digital_resale_conditions: "Œuvre numérique : conditions de revente",
  digital_transmission_conditions: "Œuvre numérique : conditions de transmission",
  digital_display_equipment: "Œuvre numérique : équipement d'affichage",
  digital_access_custodian: "Œuvre numérique : qui détient l'accès",
  digital_access_procedure: "Œuvre numérique : procédure d'accès (jamais de mot de passe)",
  digital_heir_instructions: "Œuvre numérique : instructions aux héritiers",
};


// ─── Infobulles explicatives pour les champs sensibles ────────────────────────
const FIELD_HINTS = {
  is_public_for_sale: "Par sécurité, ce champ est réglé sur Non par défaut. Aucune œuvre ne sera visible sur la marketplace sans votre accord explicite.",
  price: "Prix de référence, distinct du prix d'acquisition. Peut correspondre au prix d'achat initial, au prix coté, ou à toute valeur de référence historique que vous souhaitez conserver.",
  availability: "Statut actuel de l'œuvre dans votre collection. 'En collection' est le statut standard pour une œuvre conservée.",
  acquisition_type: "Comment l'œuvre est entrée dans votre patrimoine. Si elle vient d'un don ou d'un héritage, cela peut avoir des implications fiscales.",
  provenance: "Historique de propriété de l'œuvre avant vous. Important pour l'authenticité et la valeur. Vous pouvez mettre le nom de la galerie ou du vendeur.",
  inventory_number: "Référence unique de l'œuvre dans votre inventaire. Artkiv l'utilise pour éviter les doublons. Si votre fichier utilise la même référence pour plusieurs œuvres (lots), vous pouvez auto-numéroter.",
  currency: "Devise dans laquelle sont exprimées les valeurs financières de cet import.",
  external_id: "Avec la plateforme d'origine, identifie l'œuvre : une œuvre déjà importée avec la même référence est mise à jour au lieu d'être dupliquée.",
  digital_access_procedure: "Indiquez où se trouve l'accès et la procédure à suivre. Un champ qui contient un mot de passe, une clé ou une phrase de récupération fait refuser l'import.",
};


// ─── Champs avec valeur fixe possible ────────────────────────────────────────
const PRESET_FIELDS = {
  acquisition_type:    { options: ["Achat","Don","Héritage","Legs","Échange","Autre"] },
  acquisition_source:  { options: ["Galerie","Maison de vente","Particulier","Succession","Artiste","Autre"] },
  provenance:          { options: ["Galerie","Maison de vente","Particulier","Succession","Artiste","Autre"] },
  availability:        { options: ["En collection","En prêt","En vente","Vendu","Perdu","Volé"] },
  isEdition:           { boolean: true, options: ["Non","Oui"] },
  signature:           { boolean: true, options: ["Non","Oui"] },
  currency:            { options: ["EUR","USD","GBP","CHF","JPY","CNY"] },
};

function parseBoolean(val) {
  if (val === true  || val === "true"  || val === "Oui" || val === "1") return true;
  if (val === false || val === "false" || val === "Non" || val === "0") return false;
  return null;
}

function parseSheet(ws) {
  // Lecture brute : chaque ligne = tableau de valeurs, sans nommage auto
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  // Trouver la ligne d'en-têtes (la plus dense avec valeurs uniques)
  let bestIdx = 0, bestScore = -1;
  rawRows.slice(0, 10).forEach((row, i) => {
    const nonEmpty = row.filter((c) => c !== "" && c !== null && c !== undefined);
    const unique = new Set(nonEmpty.map((c) => String(c).trim().toLowerCase()));
    const score = nonEmpty.length + (unique.size === nonEmpty.length ? nonEmpty.length : 0);
    if (score > bestScore) { bestScore = score; bestIdx = i; }
  });

  // Construire les en-têtes depuis cette ligne, en ignorant les colonnes vides
  const headerRow = rawRows[bestIdx];
  // On garde uniquement les indices qui ont un en-tête non-vide
  const colMap = []; // [{idx, name}]
  headerRow.forEach((cell, idx) => {
    const name = String(cell).trim();
    if (name) colMap.push({ idx, name });
  });

  // Construire les objets pour les lignes de données (après l'en-tête)
  const dataRows = rawRows.slice(bestIdx + 1);
  return dataRows
    .map((row) => {
      const obj = {};
      colMap.forEach(({ idx, name }) => {
        obj[name] = row[idx] ?? "";
      });
      return obj;
    })
    // Ignorer les lignes complètement vides
    .filter((obj) => Object.values(obj).some((v) => v !== "" && v !== null && v !== undefined));
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  select: {
    flex: 1, border: "1px solid #e5e5e5", borderRadius: 2,
    padding: "0.3rem 0.5rem", fontSize: "0.85rem",
    fontFamily: "Georgia, serif", background: "#fff",
  },
  label: { width: 220, fontSize: "0.85rem", color: "#444", flexShrink: 0 },
  box: { border: "1px solid #e5e5e5", borderRadius: 2, padding: "1.25rem" },
  sectionTitle: { fontWeight: 400, fontSize: "0.95rem", letterSpacing: "0.04em", marginBottom: "1rem" },
  hint: { fontSize: "0.78rem", color: "#aaa", marginTop: "0.75rem" },
  specialBox: {
    gridColumn: "1 / -1", border: "1px solid #f0e8d0",
    borderRadius: 2, padding: "0.75rem 1rem", background: "#fffdf7",
  },
  toggleBtn: (active) => ({
    padding: "3px 12px", borderRadius: 2, fontSize: "0.8rem",
    border: active ? "1px solid #1a1a1a" : "1px solid #ccc",
    background: active ? "#1a1a1a" : "#fff",
    color: active ? "#fff" : "#666",
    cursor: "pointer", fontFamily: "Georgia, serif",
  }),
  locTag: (ok) => ({
    display: "inline-block", padding: "2px 10px", borderRadius: 2, fontSize: "0.78rem",
    border: `1px solid ${ok ? "#b5d5b0" : "#f0c080"}`,
    background: ok ? "#f0faf0" : "#fffbf0",
    color: ok ? "#3a7a35" : "#a06010",
    marginRight: 6, marginBottom: 4,
  }),
};

// ═══════════════════════════════════════════════════════════════════════════════
export default function ImportCSVPremium({ collectionId, onImportSuccess }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const T = useMemo(() => makeT(lang), [lang]);
  const fl = (tf) => (FIELD_LABELS[tf] ? T(FIELD_LABELS[tf]) : tf);
  const [workbook, setWorkbook] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [crossSheetDups, setCrossSheetDups] = useState(new Set()); // REF présentes dans un onglet déjà importé
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetRows, setSheetRows] = useState([]);
  const [mapping, setMapping] = useState({});

  // Artiste
  const [artistMode, setArtistMode] = useState("single");
  const [artistFirst, setArtistFirst] = useState("");
  const [artistLast, setArtistLast] = useState("");

  // Lieux
  const [existingLocations, setExistingLocations] = useState([]);
  const [locationPreview, setLocationPreview] = useState([]);

  // Valeurs fixes pour les champs preset
  const [fixedValues, setFixedValues] = useState({});
  // Mode par champ preset: "none" | "fixed" | "column"
  const [presetMode, setPresetMode] = useState({});

  // Gestion des doublons de numéro d'inventaire
  const [dupStrategy, setDupStrategy] = useState("autonumber"); // "autonumber" | "skip"
  const [dupCount, setDupCount] = useState(0); // nb de REF en doublon détectés
  const [applySheetSuffix, setApplySheetSuffix] = useState(false); // suffixe onglet sur les REF

  // Mode enrichissement : UPDATE des fiches existantes par inventory_number
  const [enrichMode, setEnrichMode] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null); // {updated, notFound, skipped}

  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null); // {count, sheetName}

  // Rapprochement automatique des colonnes : champ → colonne proposée (pour signaler « reconnu automatiquement »)
  const [autoMapped, setAutoMapped] = useState({});
  // Références déjà présentes dans les œuvres de l'utilisateur (parmi celles du fichier) : ces lignes seraient ignorées
  const [existingRefs, setExistingRefs] = useState(() => new Set());
  // Incident pendant l'import : { message, imported, from, jobId, entries } — permet de reprendre sans tout refaire
  const [importProblem, setImportProblem] = useState(null);
  // Annulation d'un import depuis l'écran (même fonction que le journal) et rafraîchissement du journal
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoMsg, setUndoMsg] = useState("");
  const [journalKey, setJournalKey] = useState(0);

  const headers = useMemo(
    () => (sheetRows.length ? Object.keys(sheetRows[0] || {}) : []),
    [sheetRows]
  );

  // Charger les lieux existants
  useEffect(() => {
    supabase.from("locations").select("id, name").order("name")
      .then(({ data }) => { if (data) setExistingLocations(data); });
  }, []);

  // Aperçu des lieux
  useEffect(() => {
    const col = mapping["location_name"];
    if (!col || !sheetRows.length) { setLocationPreview([]); return; }
    const names = [...new Set(sheetRows.map((r) => String(r[col] || "").trim()).filter(Boolean))];
    setLocationPreview(names.map((name) => ({
      name,
      exists: existingLocations.some((l) => l.name.trim().toLowerCase() === name.toLowerCase()),
    })));
  }, [mapping, sheetRows, existingLocations]);

  // Détecter les doublons de numéro d'inventaire
  useEffect(() => {
    const invCol = mapping["inventory_number"];
    if (!invCol || !sheetRows.length) { setDupCount(0); return; }
    const refs = sheetRows.map((r) => String(r[invCol] || "").trim()).filter(Boolean);
    const counts = refs.reduce((acc, r) => { acc[r] = (acc[r] || 0) + 1; return acc; }, {});
    const dups = Object.values(counts).filter((v) => v > 1).length;
    setDupCount(dups);
  }, [mapping, sheetRows]);

  // Références du fichier déjà présentes dans les œuvres de l'utilisateur (le numéro d'inventaire est unique par compte)
  useEffect(() => {
    const invCol = mapping["inventory_number"];
    if (!invCol || !sheetRows.length) { setExistingRefs(new Set()); return undefined; }
    let alive = true;
    (async () => {
      const refs = [...new Set(sheetRows.map((r) => String(r[invCol] ?? "").trim()).filter(Boolean))];
      const found = new Set();
      const { data: who } = await supabase.auth.getSession();
      const uid = who?.session?.user?.id;
      if (!uid) { if (alive) setExistingRefs(new Set()); return; }
      for (let i = 0; i < refs.length; i += 200) {
        const { data } = await withoutProposalWorks(supabase.from("artworks").select("inventory_number").eq("client_id", uid).in("inventory_number", refs.slice(i, i + 200)), uid);
        (data || []).forEach((a) => found.add(String(a.inventory_number).trim()));
      }
      if (alive) setExistingRefs(found);
    })().catch(() => { if (alive) setExistingRefs(new Set()); });
    return () => { alive = false; };
  }, [mapping, sheetRows]);

  // Contrôle avant import : rien n'est écrit
  const analysis = useMemo(
    () => (sheetRows.length ? analyzeRows(sheetRows, { mapping, artistMode, artistFirst, artistLast, existingRefs, dupStrategy, t: T }) : null),
    [sheetRows, mapping, artistMode, artistFirst, artistLast, existingRefs, dupStrategy, T]
  );

  async function undoImport(jobId, count) {
    if (!jobId) return;
    const ok = window.confirm(T("Annuler cet import et supprimer les {count} œuvre(s) créées ? Action irréversible.", { count }));
    if (!ok) return;
    setUndoBusy(true); setUndoMsg("");
    try {
      const { data, error } = await supabase.functions.invoke("import_undo", { body: { job_id: jobId } });
      if (error || !data?.ok) throw error || new Error("undo");
      setUndoMsg(
        T("Import annulé : {deleted} œuvre(s) supprimée(s).", { deleted: data.deleted }) +
        (data.failed > 0 ? T(" {failed} n'ont pas pu être supprimées : réessayez ou contactez-nous.", { failed: data.failed }) : "") +
        T(" Les fichiers déjà envoyés dans le stockage ne sont pas supprimés.")
      );
      setImportResult(null);
      setImportProblem(null);
      setJournalKey((k) => k + 1);
    } catch {
      setUndoMsg(T("L'annulation a échoué. Vous pouvez réessayer depuis le journal des imports ci-dessus."));
    } finally {
      setUndoBusy(false);
    }
  }

  function downloadReport(entries, filename) {
    const blob = new Blob([reportToCsv(entries, T)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  // Lecture fichier
  async function handleFile(file) {
    setImportFileName(file?.name || "");
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: "array" });
    setWorkbook(wb);
    setSheetNames(wb.SheetNames);
    setArtistMode("single"); setArtistFirst(""); setArtistLast("");
    if (wb.SheetNames.length === 1) loadSheet(wb, wb.SheetNames[0]);
    else { setSelectedSheet(null); setSheetRows([]); setMapping({}); }
  }

  function loadSheet(wb, name) {
    const json = parseSheet(wb.Sheets[name]);
    setSelectedSheet(name);
    setSheetRows(json);
    const headersOfSheet = json.length ? Object.keys(json[0]) : [];
    const { mapping: suggested } = suggestMapping(headersOfSheet, TARGET_FIELDS);
    setMapping(suggested);
    setAutoMapped(suggested);
    setImportProblem(null);
    setArtistMode("single"); setArtistFirst(""); setArtistLast("");
    // Pré-remplir les valeurs par défaut définies dans PRESET_FIELDS
    const defaultFixed = {};
    const defaultMode = {};
    Object.entries(PRESET_FIELDS).forEach(([field, cfg]) => {
      if (cfg.defaultFixed !== undefined) {
        defaultFixed[field] = cfg.defaultFixed;
        defaultMode[field] = "fixed";
      }
    });
    // Les champs à valeurs prédéfinies rapprochés automatiquement passent en mode « Colonne » pour que ce soit visible.
    Object.keys(suggested).forEach((f) => { if (PRESET_FIELDS[f]) defaultMode[f] = "column"; });
    setFixedValues(defaultFixed);
    setPresetMode(defaultMode);
  }

  // Résoudre les lieux connus côté client — les inconnus seront créés par l'Edge Function
  function resolveLocations(names) {
    const result = {};
    names.forEach((name) => {
      const existing = existingLocations.find(
        (l) => l.name.trim().toLowerCase() === name.trim().toLowerCase()
      );
      // Stocker DEUX clés : nom exact ET lowercase pour éviter les problèmes de casse
      if (existing) {
        result[name.trim()] = existing.id;
        result[name.trim().toLowerCase()] = existing.id;
      }
    });
    return result;
  }

  // Import
  async function submit(resume = null) {
    if (!sheetRows.length) { alert(T("Aucune donnée à importer.")); return; }
    if (!collectionId) { alert(T("Sélectionnez une collection cible.")); return; }
    setImportProblem(null);
    setLoading(true);

    try {
      // Résoudre lieux
      const locationCol = mapping["location_name"];
      const locationNames = locationCol
        ? [...new Set(sheetRows.map((r) => String(r[locationCol] || "").trim()).filter(Boolean))]
        : [];
      const locationMap = locationNames.length ? await resolveLocations(locationNames) : {};

      // ── Appliquer la stratégie doublons sur inventory_number ──────────────
      const invCol = mapping["inventory_number"];
      const refCounter = {};
      const processedRows = sheetRows.map((r) => {
        if (!invCol) return r;
        const ref = String(r[invCol] || "").trim();
        if (!ref) return r;
        refCounter[ref] = (refCounter[ref] || 0) + 1;
        if (refCounter[ref] === 1) return r; // première occurrence → inchangée
        // doublon dans ce fichier
        if (dupStrategy === "autonumber") {
          return { ...r, [invCol]: `${ref}-${refCounter[ref]}` };
        } else {
          return { ...r, __skip: true };
        }
      }).filter((r) => !r.__skip);

      // ── Suffixe d'onglet pour éviter les conflits inter-onglets ─────────
      // Si l'onglet a un nom (autre que le 1er onglet), on suffixe les REF
      // avec une abréviation de l'onglet pour éviter les doublons en base
      const sheetSuffix = selectedSheet && sheetNames.length > 1
        ? "-" + selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()
        : null;

      const allRows = processedRows.map((r) => {
        const obj = {};

        TARGET_FIELDS.forEach((tf) => {
          if (tf === "artist" || tf === "location_name") return;
          const preset = PRESET_FIELDS[tf];
          if (preset) {
            // colonne en priorité, sinon valeur fixe, sinon null
            const fromCol = mapping[tf] ? cleanValue(r[mapping[tf]]) : null;
            const fromFixed = fixedValues[tf] ?? null;
            const raw = fromCol || fromFixed;
            obj[tf] = preset.boolean ? parseBoolean(raw) : (raw || null);
          } else {
            const src = mapping[tf];
            obj[tf] = cleanValue(src ? r[src] : null);
          }
        });

        // Suffixe onglet sur inventory_number — uniquement si c'est le 2e onglet ou plus
        // ET que la REF est dans la liste des doublons inter-onglets
        if (sheetSuffix && invCol && obj.inventory_number && crossSheetDups.has(obj.inventory_number)) {
          obj.inventory_number = obj.inventory_number + sheetSuffix;
        }

        // Artiste
        obj.artist = artistMode === "merge"
          ? [artistFirst ? String(r[artistFirst] || "").trim() : "",
             artistLast  ? String(r[artistLast]  || "").trim() : ""]
              .filter(Boolean).join(" ") || null
          : cleanValue(mapping["artist"] ? r[mapping["artist"]] : null);

        // Lieu → UUID (cherche en exact puis en lowercase)
        if (locationCol) {
          const locName = String(r[locationCol] || "").trim();
          obj.location_id = locName
            ? (locationMap[locName] || locationMap[locName.toLowerCase()] || null)
            : null;
          // Conserver le nom pour que l'Edge Function puisse créer si UUID non trouvé
          if (locName) obj.location_name = locName;
        }

        // Normaliser virgule décimale française dans dimensions (trigger Postgres en a besoin)
        if (obj.dimensions) {
          obj.dimensions = String(obj.dimensions).replace(/,([0-9])/g, ".$1");
        }

        // Dimensions : parser la colonne texte → colonnes plates + specifications JSON
        const d = parseDimensions(obj.dimensions);
        // Colonnes plates (utilisées par l'onglet Spécifications)
        obj.dimensions_height = d.height;
        obj.dimensions_width  = d.width;
        obj.dimensions_depth  = d.depth;
        obj.dimensions_unit   = d.unit;
        // JSONB specifications (utilisé par d'autres parties de l'app)
        obj.specifications = { dimensions: { height: d.height, width: d.width, depth: d.depth, unit: d.unit } };
        // Conserver la chaîne brute dans dimensions pour affichage éventuel
        // obj.dimensions reste inchangé

        // Tags
        obj.tags = typeof obj.tags === "string" && obj.tags
          ? obj.tags.split(",").map((s) => s.trim()).filter(Boolean) : [];

        // Catégorie médium — déduite automatiquement depuis le champ medium
        if (obj.medium && !obj.medium_category) {
          obj.medium_category = inferMediumCategory(obj.medium);
        }

        // Devise ligne par ligne : colonne « devise » (€, euros, usd…), sinon symbole écrit dans le montant (« 12 000 € »),
        // sinon valeur fixe de l'import. Une devise inconnue n'est pas enregistrée (le contrôle avant import la signale).
        {
          const fromColumn = normalizeCurrency(obj.currency);
          const fromAmount = ["estimated_value", "acquisition_price", "sale_price", "value"]
            .map((f) => currencyFromAmount(obj[f])).find(Boolean) || null;
          obj.currency = fromColumn || fromAmount || normalizeCurrency(fixedValues.currency) || null;
        }
        // Disponibilité : « Sold », « Available », « On loan »… → libellé standard ; sinon valeur d'origine conservée.
        if (obj.availability) obj.availability = normalizeAvailability(obj.availability) || obj.availability;

        // Numériques
        ["value", "price", "sale_price", "estimated_value", "acquisition_price"]
          .forEach((f) => { obj[f] = toNumber(obj[f]); });

        // Dates
        if (obj.acquisition_date) obj.acquisition_date = excelDateToISO(obj.acquisition_date);
        if (obj.last_estimation_date) obj.last_estimation_date = excelDateToISO(obj.last_estimation_date);

        // acquisitionDetails
        const hasAcq = obj.acquisition_date || obj.acquisition_price != null
          || obj.acquisition_type || obj.acquisition_source;
        if (hasAcq) {
          obj.acquisitionDetails = JSON.stringify({
            acquisition_date: obj.acquisition_date || "",
            acquisition_price: obj.acquisition_price != null ? String(obj.acquisition_price) : "",
            acquisition_type: obj.acquisition_type || "",
            acquisition_source: obj.acquisition_source || "",
          });
        }

        // Provenance
        if (obj.provenance && typeof obj.provenance === "string") {
          const t = obj.provenance.trim();
          obj.provenance = t
            ? JSON.stringify([{ id: 1, date: null, location: t, description: t }]) : null;
        }

        // Patrimoine et art numérique : statut normalisé, colonnes digital_* regroupées en JSON,
        // plateforme et identifiant externes toujours ensemble.
        obj.heritage_status = normalizeHeritageStatus(obj.heritage_status);
        const digital = {};
        Object.entries(DIGITAL_COLUMNS).forEach(([col, key]) => {
          const v = obj[col];
          if (v != null && String(v).trim() !== "") digital[key] = String(v).replace(/\r\n|\r|\n/g, " ").trim();
          delete obj[col];
        });
        obj.digital_asset = Object.keys(digital).length ? digital : null;
        obj.external_source = obj.external_source != null ? (String(obj.external_source).trim().toLowerCase() || null) : null;
        obj.external_id = obj.external_id != null ? (String(obj.external_id).trim() || null) : null;
        if (!obj.external_source || !obj.external_id) { obj.external_source = null; obj.external_id = null; }
        // Traçabilité de la source : sans référence externe, ces champs n'ont pas de sens.
        {
          const url = obj.source_url != null ? String(obj.source_url).trim() : "";
          const when = obj.source_updated_at ? new Date(obj.source_updated_at) : null;
          const coll = obj.source_collection != null ? String(obj.source_collection).trim().slice(0, 120) : "";
          const hasRef = !!(obj.external_source && obj.external_id);
          obj.source_url = hasRef && /^https?:\/\//i.test(url) && url.length <= 500 ? url : null;
          obj.source_updated_at = hasRef && when && !Number.isNaN(when.getTime()) ? when.toISOString() : null;
          obj.source_collection = hasRef && coll ? coll : null;
          obj.imported_at = hasRef ? new Date().toISOString() : null;
        }

        // title NOT NULL
        if (!obj.title || String(obj.title).trim() === "") obj.title = T("Sans titre");

        // Nettoyer les retours chariot dans les champs texte
        ["title","artist","medium","description","condition","conditionNotes",
         "acquisition_type","acquisition_source","typeOfArt","availability"].forEach((f) => {
          if (obj[f] && typeof obj[f] === "string") {
            obj[f] = obj[f].replace(/\r\n|\r|\n/g, " ").trim() || null;
          }
        });

        // Valeurs forcées par défaut
        obj.is_public_for_sale = false;

        obj.collection_id = collectionId;
        return obj;
      });
      let rows = allRows;

      // ── Aucun secret d'accès dans les champs d'œuvre numérique ──
      const secretRows = [];
      let warnRows = 0;
      allRows.forEach((o, idx) => {
        const levels = Object.values(o.digital_asset || {}).map(secretLevel);
        if (levels.includes("block")) secretRows.push(idx + 1);
        else if (levels.includes("warn")) warnRows += 1;
      });
      if (secretRows.length) {
        alert(T(
          "Import refusé : une colonne « œuvre numérique » semble contenir un mot de passe, une clé ou une phrase de récupération (lignes {rows}). ARTICHAUT ne doit jamais les conserver : indiquez seulement où se trouve l'accès et la procédure à suivre.",
          { rows: `${secretRows.slice(0, 10).join(", ")}${secretRows.length > 10 ? "…" : ""}` }
        ));
        return;
      }
      if (warnRows && !window.confirm(
        T("{n} ligne(s) contiennent une suite de mots qui pourrait être une phrase de récupération. ARTICHAUT ne doit jamais la conserver. Importer quand même ?", { n: warnRows })
      )) return;

      // ── Références externes : doublons du fichier ignorés, œuvres déjà connues mises à jour ──
      let updatedExisting = 0;
      let duplicatesInFile = 0;
      const withRef = allRows.filter((o) => o.external_source && o.external_id);
      if (withRef.length) {
        const { data: who } = await supabase.auth.getSession();
        const uid = who?.session?.user?.id || null;

        const seen = new Set();
        const unique = [];
        for (const o of allRows) {
          if (o.external_source && o.external_id) {
            const key = `${o.external_source}|${o.external_id}`;
            if (seen.has(key)) { duplicatesInFile += 1; continue; }
            seen.add(key);
          }
          unique.push(o);
        }

        const ids = [...new Set(withRef.map((o) => o.external_id))];
        const known = new Map();
        for (let i = 0; i < ids.length; i += 200) {
          let q = supabase
            .from("artworks")
            .select("id, external_source, external_id, digital_asset")
            .in("external_id", ids.slice(i, i + 200));
          if (uid) q = applyProposalFilter(q.eq("client_id", uid), await proposalCollectionIds(uid));
          const { data: found, error: findErr } = await q;
          if (findErr) throw findErr;
          (found || []).forEach((a) => known.set(`${a.external_source}|${a.external_id}`, a));
        }

        const toInsert = [];
        for (const o of unique) {
          const key = o.external_source && o.external_id ? `${o.external_source}|${o.external_id}` : null;
          const existing = key ? known.get(key) : null;
          if (!existing) { toInsert.push(o); continue; }
          const patch = {};
          EXTERNAL_UPDATABLE.forEach((f) => { if (o[f] != null && o[f] !== "") patch[f] = o[f]; });
          if (o.digital_asset) patch.digital_asset = { ...(existing.digital_asset || {}), ...o.digital_asset };
          if (Object.keys(patch).length) {
            const { error: upErr } = await supabase.from("artworks").update(patch).eq("id", existing.id);
            if (upErr) throw upErr;
          }
          updatedExisting += 1;
        }
        rows = toInsert;
      }

      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;

      // Lieux manquants (pas encore en base) → l'Edge Function les créera
      const missingLocations = locationNames.filter((n) => !locationMap[n]);

      // Import par chunks de 50 pour éviter le timeout de l'Edge Function
      const CHUNK_SIZE = 50;
      let totalInserted = resume ? resume.imported : 0;
      let firstChunk = !resume || resume.from === 0;
      // UN seul job d'import pour tout le fichier : créé au 1er chunk, réutilisé ensuite
      // (journal d'import + annulation, ARTDAI §6). En cas de reprise, on garde le même journal.
      let importJobId = resume ? resume.jobId : null;
      const serverErrors = resume ? [...(resume.serverErrors || [])] : [];
      let skippedByServer = resume ? (resume.skipped || 0) : 0;

      for (let i = resume ? resume.from : 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        // Les lieux manquants sont créés seulement au premier chunk
        const { data, error } = await supabase.functions.invoke("import_artworks_self", {
          body: {
            rows: chunk,
            collection_id: collectionId,
            missing_locations: firstChunk ? missingLocations : [],
            source_filename: importFileName || null,
            total_rows: rows.length,
            mapping: mapping || null,
            job_id: importJobId,
            row_offset: i,
          },
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });

        if (error || !data || data.ok === false) {
          // Incident : on s'arrête, on explique et on propose de reprendre là où ça s'est arrêté (sans doublonner ce qui est passé).
          const reason = error?.message || data?.error || T("Erreur inconnue");
          setImportProblem({
            message: T("L'envoi s'est interrompu aux lignes {from} à {to} sur {total} : {reason}", { from: i + 1, to: Math.min(i + CHUNK_SIZE, rows.length), total: rows.length, reason }),
            imported: totalInserted,
            total: rows.length,
            from: i,
            jobId: importJobId,
            serverErrors,
            skipped: skippedByServer,
          });
          return;
        }
        if (data.job_id) importJobId = data.job_id;
        const inserted = data.inserted_count || 0;
        const errs = Array.isArray(data.errors) ? data.errors : [];
        totalInserted += inserted;
        // Lignes que le serveur n'a pas enregistrées sans erreur : doublons d'inventaire ou de référence externe déjà connus.
        skippedByServer += Math.max(0, chunk.length - inserted - errs.length);
        errs.forEach((m) => serverErrors.push(m));
        firstChunk = false;
      }

      // Mémoriser les REF de cet onglet pour détecter les doublons inter-onglets
      const importedRefs = new Set(rows.map(r => r.inventory_number).filter(Boolean));
      setCrossSheetDups(prev => new Set([...prev, ...importedRefs]));
      // Si la fonction d'import n'enregistre pas encore les références externes (non déployée), le dire.
      let fieldWarning = null;
      const sentWithRef = rows.filter((o) => o.external_id);
      if (sentWithRef.length && totalInserted > 0) {
        const probe = sentWithRef.slice(0, 50).map((o) => o.external_id);
        const { data: saved } = await supabase
          .from("artworks").select("id").eq("collection_id", collectionId).in("external_id", probe).limit(1);
        if (!(saved || []).length) {
          fieldWarning = T("Les colonnes plateforme / identifiant externe, statut patrimonial et œuvre numérique n'ont pas été enregistrées : la fonction d'import doit être mise à jour.");
        }
      }
      setImportResult({
        count: totalInserted, sheetName: selectedSheet, updated: updatedExisting, duplicatesInFile, warning: fieldWarning,
        skipped: skippedByServer, errors: serverErrors, jobId: importJobId,
      });
      setUndoMsg("");
      setJournalKey((k) => k + 1);
      // Recharger les lieux pour que le 2e onglet bénéficie des nouveaux lieux créés
      const { data: freshLocs } = await supabase.from("locations").select("id, name").order("name");
      if (freshLocs) setExistingLocations(freshLocs);
      onImportSuccess?.();
    } catch (e) {
      setImportProblem({ message: e?.message || T("Erreur inconnue pendant l'import."), imported: 0, total: sheetRows.length, from: null });
    } finally {
      setLoading(false);
    }
  }

  // ─── Mode enrichissement : UPDATE par inventory_number ───────────────────
  async function handleEnrich() {
    if (!collectionId || !sheetRows.length || !mapping["inventory_number"]) {
      alert(T("Le mode enrichissement nécessite une colonne 'Numéro d'inventaire' mappée."));
      return;
    }
    setLoading(true);
    setEnrichResult(null);

    try {
      const invCol = mapping["inventory_number"];
      let updated = 0, notFound = 0, skipped = 0;

      // Charger toutes les fiches de la collection avec leur inventory_number
      const { data: existingArtworks, error: fetchErr } = await supabase
        .from("artworks")
        .select("id, inventory_number")
        .eq("collection_id", collectionId);

      if (fetchErr) throw fetchErr;

      // Index par inventory_number pour lookup rapide
      const artworkIndex = {};
      for (const aw of existingArtworks || []) {
        if (aw.inventory_number) {
          artworkIndex[aw.inventory_number.trim().toLowerCase()] = aw.id;
        }
      }

      // Traiter chaque ligne du tableur
      for (const r of sheetRows) {
        const invRef = String(r[invCol] || "").trim();
        if (!invRef) { skipped++; continue; }

        const artworkId = artworkIndex[invRef.toLowerCase()];
        if (!artworkId) { notFound++; continue; }

        // Construire le patch avec uniquement les champs mappés non vides
        const patch = {};
        const UPDATABLE = [
          "title", "artist", "year", "medium", "dimensions", "description",
          "condition", "conditionNotes", "typeOfArt", "subjectMatter",
          "availability", "estimated_value", "value", "currency",
          "acquisition_date", "acquisition_price", "acquisition_type", "acquisition_source",
          "dimensions_height", "dimensions_width", "dimensions_depth", "dimensions_unit",
          "tags", "styles", "signature",
        ];

        UPDATABLE.forEach((tf) => {
          const src = mapping[tf];
          if (!src) return;
          const raw = cleanValue(r[src]);
          if (raw == null || raw === "") return;
          patch[tf] = raw;
        });

        // Artiste
        if (artistMode === "merge" && (artistFirst || artistLast)) {
          const name = [
            artistFirst ? String(r[artistFirst] || "").trim() : "",
            artistLast  ? String(r[artistLast]  || "").trim() : "",
          ].filter(Boolean).join(" ");
          if (name) patch.artist = name;
        } else if (mapping["artist"]) {
          const a = cleanValue(r[mapping["artist"]]);
          if (a) patch.artist = a;
        }

        // Numériques
        ["value", "estimated_value", "acquisition_price"].forEach((f) => {
          if (patch[f] != null) patch[f] = toNumber(patch[f]);
        });

        // Dates
        if (patch.acquisition_date) patch.acquisition_date = excelDateToISO(patch.acquisition_date);

        // Dimensions → colonnes plates
        if (patch.dimensions) {
          patch.dimensions = String(patch.dimensions).replace(/,([0-9])/g, ".$1");
          const d = parseDimensions(patch.dimensions);
          patch.dimensions_height = d.height;
          patch.dimensions_width  = d.width;
          patch.dimensions_depth  = d.depth;
          patch.dimensions_unit   = d.unit;
          patch.specifications    = { dimensions: d };
        }

        // Tags
        if (patch.tags && typeof patch.tags === "string") {
          patch.tags = patch.tags.split(",").map(s => s.trim()).filter(Boolean);
        }

        // acquisitionDetails
        const hasAcq = patch.acquisition_date || patch.acquisition_price != null
          || patch.acquisition_type || patch.acquisition_source;
        if (hasAcq) {
          patch.acquisitionDetails = {
            date:   patch.acquisition_date   || null,
            price:  patch.acquisition_price  ?? null,
            type:   patch.acquisition_type   || null,
            source: patch.acquisition_source || null,
          };
        }

        if (Object.keys(patch).length === 0) { skipped++; continue; }

        const { error: upErr } = await supabase
          .from("artworks")
          .update(patch)
          .eq("id", artworkId);

        if (upErr) {
          console.error("[Enrich] update failed for", invRef, upErr);
          skipped++;
        } else {
          updated++;
        }
      }

      setEnrichResult({ updated, notFound, skipped });
      onImportSuccess?.();
    } catch (e) {
      alert(T("Erreur enrichissement : {msg}", { msg: e.message }));
    } finally {
      setLoading(false);
    }
  }

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Georgia, serif" }} className="space-y-6">

      {/* Journal des imports + annulation (ARTDAI §6) — ne s'affiche que s'il y en a */}
      <ImportJobsList key={journalKey} />

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 style={{ fontSize: "1.1rem", letterSpacing: "0.04em", fontWeight: 400 }}>
          {T("Import CSV / XLSX")}
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle mode enrichissement */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => { setEnrichMode(v => !v); setEnrichResult(null); }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enrichMode ? "bg-black" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enrichMode ? "translate-x-4" : "translate-x-1"}`} />
            </div>
            <span style={{ fontSize: "0.85rem", color: enrichMode ? "#000" : "#888" }}>
              {enrichMode ? T("Mode : Enrichir les fiches existantes") : T("Mode : Créer de nouvelles fiches")}
            </span>
          </label>
          <button className="btn-artsy btn-secondary-artsy" onClick={() => {
            downloadImportTemplate(XLSX, lang);
          }}>
            {T("Télécharger un modèle")}
          </button>
        </div>
      </div>

      {/* Bandeau explication mode enrichissement */}
      {enrichMode && (
        <div style={{ border: "1px solid #c9a961", borderRadius: 4, padding: "0.875rem 1.25rem", background: "#fffbf2", fontSize: "0.85rem", color: "#7a5a10" }}>
          <strong>{T("Mode enrichissement :")}</strong>{T(" les données du tableur viennent ")}<em>{T("compléter")}</em>{T(" les fiches déjà créées via l'import ZIP.")}
          {T(" La correspondance se fait par le ")}<strong>{T("numéro d'inventaire")}</strong>{T(" — mappez cette colonne ci-dessous.")}
          {T(" Seuls les champs non vides du tableur seront mis à jour.")}
        </div>
      )}

      {/* ── Explication pédagogique (visible tant qu'aucun fichier chargé) ── */}
      {!sheetRows.length && (
        <div style={{ border: "1px solid #e5e5e5", borderRadius: 2, padding: "1.5rem 2rem" }}>

          {/* Intro */}
          <p style={{ fontSize: "0.9rem", color: "#444", lineHeight: 1.7, marginBottom: "1.5rem" }}>
            {T("Cet outil vous permet d'importer en une seule opération l'ensemble de vos œuvres depuis un fichier Excel ou CSV — qu'il vienne d'une galerie, d'un notaire, ou de votre propre tableur. Aucune connaissance technique requise.")}
          </p>

          {/* 3 étapes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.5rem", marginBottom: "1.5rem" }}>
            {[
              {
                num: "1",
                titre: T("Choisissez votre fichier"),
                texte: T("Formats acceptés : Excel (.xlsx, .xls) ou CSV. Si votre fichier contient plusieurs onglets, vous choisirez lequel importer. Les colonnes vides en tête de fichier sont ignorées automatiquement."),
              },
              {
                num: "2",
                titre: T("Associez vos colonnes"),
                texte: T("Les colonnes dont le nom est courant (titre, artiste, année, technique, dimensions, n° d'inventaire, lieu…, en français ou en anglais) sont reconnues automatiquement. Vous vérifiez et ajustez si besoin. Pour l'artiste, vous pouvez fusionner deux colonnes Nom + Prénom en un seul champ."),
              },
              {
                num: "3",
                titre: T("Importez en un clic"),
                texte: T("Un contrôle avant import indique combien de fiches seront créées, ignorées ou à vérifier, ligne par ligne, sans rien écrire. En cas d'incident, vous reprenez là où l'envoi s'est arrêté. Les lieux inconnus sont créés automatiquement."),
              },
            ].map(({ num, titre, texte }) => (
              <div key={num} style={{ display: "flex", gap: "1rem" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", background: "#1a1a1a",
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.8rem", flexShrink: 0, marginTop: 2,
                }}>
                  {num}
                </div>
                <div>
                  <p style={{ fontSize: "0.85rem", fontWeight: 500, color: "#1a1a1a", marginBottom: "0.35rem" }}>
                    {titre}
                  </p>
                  <p style={{ fontSize: "0.8rem", color: "#888", lineHeight: 1.6 }}>
                    {texte}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Formats acceptés */}
          <div style={{ background: "#faf9f6", border: "1px solid #ece9e3", borderRadius: 2, padding: "0.75rem 1rem" }}>
            <p style={{ fontSize: "0.78rem", color: "#888", lineHeight: 1.6 }}>
              <strong style={{ color: "#444" }}>{T("Formats acceptés :")}</strong>{T(" Excel 97–2019 (.xls, .xlsx), CSV (séparateur virgule ou point-virgule).")}
              {" "}<strong style={{ color: "#444" }}>{T("Taille max recommandée :")}</strong>{T(" 2 000 œuvres par import.")}
              {T(" Si votre fichier vient d'une galerie ou d'une maison de vente, importez-le tel quel — Artkiv s'adapte à sa structure.")}
            </p>
          </div>

          {/* Lien modèle */}
          <p style={{ fontSize: "0.78rem", color: "#aaa", marginTop: "0.75rem" }}>
            {T("Vous partez de zéro ?")}{" "}
            <button
              style={{ background: "none", border: "none", color: "#c9a961", cursor: "pointer", fontSize: "0.78rem", padding: 0, textDecoration: "underline" }}
              onClick={() => {
                downloadImportTemplate(XLSX, lang);
              }}
            >
              {T("Téléchargez notre modèle Excel")}
            </button>
            {" "}{T("et remplissez-le directement.")}
          </p>
        </div>
      )}

      {/* ── Zone de dépôt fichier ─────────────────────────────────────────── */}
      <label style={{
        display: "block", border: "1px dashed #c9a961", borderRadius: 2,
        padding: sheetRows.length ? "1rem 2rem" : "2rem",
        textAlign: "center", cursor: "pointer",
        background: "#faf9f6", color: "#888", fontSize: "0.9rem",
        transition: "padding 0.2s",
      }}>
        <input type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }}
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
        {sheetRows.length
          ? T("Cliquez pour changer de fichier")
          : T("Cliquez pour choisir un fichier .xlsx ou .csv")}
      </label>

      {/* Sélecteur onglets */}
      {workbook && sheetNames.length > 1 && (
        <div style={{ background: "#fff8ee", border: "1px solid #c9a961", borderRadius: 2, padding: "1rem 1.25rem" }}>
          <p style={{ fontSize: "0.85rem", color: "#888", marginBottom: "0.75rem" }}>
            {T("Votre fichier contient plusieurs onglets. Lequel contient vos œuvres ?")}
          </p>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {sheetNames.map((name) => (
              <button key={name} onClick={() => loadSheet(workbook, name)}
                style={S.toggleBtn(selectedSheet === name)}>
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mapping */}
      {sheetRows.length > 0 && (
        <>
          <DigitalMixHint
            collectionId={collectionId}
            incomingDigital={Object.keys(mapping).some((k) => mapping[k] && (k === "external_source" || k === "external_id" || k.startsWith("digital_")))}
          />
          <div style={S.box}>
            <h4 style={S.sectionTitle}>{T("Associer vos colonnes")}</h4>
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ fontSize: "0.8rem", color: "#666", marginBottom: "0.4rem" }}>
                <strong>{T("{n} colonne(s)", { n: headers.length })}</strong>{T(" détectée(s)")}
                {selectedSheet ? T(" dans l'onglet « {sheet} »", { sheet: selectedSheet }) : ""}.
                {" "}{T("Les correspondances évidentes ont été faites automatiquement.")}
              </p>
              <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6 }}>
                {T("Pour chaque champ Artkiv (colonne de gauche), choisissez la colonne correspondante dans votre fichier. Les champs non mappés seront laissés vides — vous pourrez les compléter fiche par fiche ensuite. Les champs marqués ")}<span style={{ color: "#c9a961" }}>●</span>{T(" proposent aussi une valeur commune à appliquer à toutes les œuvres.")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-2">
              {TARGET_FIELDS.map((tf) => {

                // ── Champ Artiste ──────────────────────────────────────────
                if (tf === "artist") return (
                  <div key="artist" style={S.specialBox}>
                    <p style={{ fontSize: "0.85rem", color: "#444", fontWeight: 500, marginBottom: "0.5rem" }}>
                      {T("Artiste")}
                    </p>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                      <button style={S.toggleBtn(artistMode === "single")}
                        onClick={() => setArtistMode("single")}>
                        {T("Une seule colonne")}
                      </button>
                      <button style={S.toggleBtn(artistMode === "merge")}
                        onClick={() => setArtistMode("merge")}>
                        {T("Fusionner Nom + Prénom")}
                      </button>
                    </div>

                    <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6, marginBottom: "0.6rem" }}>
                      {T("Si votre fichier a une seule colonne \"Artiste\" avec le nom complet, choisissez ")}<em>{T("Une seule colonne")}</em>{T(". Si les colonnes Nom et Prénom sont séparées (fréquent dans les inventaires de galeries), choisissez ")}<em>{T("Fusionner")}</em>{T(" — Artkiv les assemblera automatiquement.")}
                    </p>

                    {artistMode === "single" ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={S.label}>{T("Colonne artiste")}</div>
                        <select style={S.select} value={mapping["artist"] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, artist: e.target.value || undefined }))}>
                          <option value="">{T("— ne pas importer")}</option>
                          {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={S.label}>{T("Colonne Prénom")}</div>
                          <select style={S.select} value={artistFirst}
                            onChange={(e) => setArtistFirst(e.target.value)}>
                            <option value="">—</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={S.label}>{T("Colonne Nom")}</div>
                          <select style={S.select} value={artistLast}
                            onChange={(e) => setArtistLast(e.target.value)}>
                            <option value="">—</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                        {(artistFirst || artistLast) && sheetRows[0] && (
                          <p style={{ fontSize: "0.8rem", color: "#888", marginTop: 2 }}>
                            {T("Aperçu 1re ligne :")}{" "}
                            <strong style={{ color: "#1a1a1a" }}>
                              {[
                                artistFirst ? String(sheetRows[0][artistFirst] || "").trim() : "",
                                artistLast  ? String(sheetRows[0][artistLast]  || "").trim() : "",
                              ].filter(Boolean).join(" ")}
                            </strong>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );

                // ── Champ Lieu ─────────────────────────────────────────────
                if (tf === "location_name") return (
                  <div key="location_name" style={S.specialBox}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: locationPreview.length ? "0.75rem" : 0 }}>
                      <div style={{ fontSize: "0.85rem", color: "#444", fontWeight: 500, marginRight: 8, flexShrink: 0 }}>
                        {T("Lieu de stockage")}
                      </div>
                      <select style={{ ...S.select, maxWidth: 320 }} value={mapping["location_name"] || ""}
                        onChange={(e) => setMapping((m) => ({ ...m, location_name: e.target.value || undefined }))}>
                        <option value="">{T("— ne pas importer")}</option>
                        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>

                    <p style={{ fontSize: "0.78rem", color: "#aaa", lineHeight: 1.6, marginBottom: "0.5rem" }}>
                      {T("Indiquez la colonne qui contient le lieu de stockage (ex : \"Réserve Paris\", \"Domicile\", \"IVRY / Capstone\"). Les lieux déjà enregistrés dans Artkiv sont reconnus automatiquement (")}<span style={{color:"#3a7a35"}}>✓</span>{T("). Les nouveaux seront créés pour vous lors de l'import (")}<span style={{color:"#a06010"}}>+</span>{T(") — vous pourrez les compléter avec une adresse ensuite.")}
                    </p>

                    {locationPreview.length > 0 && (
                      <div>
                        <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: "0.4rem" }}>
                          {T("Lieux détectés dans votre fichier :")}
                        </p>
                        <div>
                          {locationPreview.map(({ name, exists }) => (
                            <span key={name} style={S.locTag(exists)}>
                              {exists ? "✓" : "+"} {name}
                            </span>
                          ))}
                        </div>
                        {locationPreview.some((l) => !l.exists) && (
                          <p style={{ fontSize: "0.75rem", color: "#a06010", marginTop: "0.4rem" }}>
                            {T("Les lieux marqués ")}<strong>+</strong>{T(" seront créés automatiquement lors de l'import.")}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );

                // ── Champs preset (valeur fixe ou colonne) ────────────
                if (PRESET_FIELDS[tf]) {
                  const preset = PRESET_FIELDS[tf];
                  const mode = presetMode[tf] || "none";
                  return (
                    <div key={tf} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={S.label}>{fl(tf)}</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {["none","fixed","column"].map((m) => (
                            <button key={m} style={{
                              ...S.toggleBtn(mode === m),
                              padding: "2px 8px", fontSize: "0.75rem",
                            }} onClick={() => setPresetMode((p) => ({ ...p, [tf]: m }))}>
                              {m === "none" ? "—" : m === "fixed" ? T("Valeur fixe") : T("Colonne")}
                            </button>
                          ))}
                        </div>
                      </div>
                      {mode === "fixed" && (
                        <div style={{ paddingLeft: 228 }}>
                          <select style={{ ...S.select, background: "#fffdf7", maxWidth: 220 }}
                            value={fixedValues[tf] || ""}
                            onChange={(e) => setFixedValues((v) => ({ ...v, [tf]: e.target.value || undefined }))}>
                            <option value="">{T("— choisir…")}</option>
                            {preset.options.map((o) => <option key={o} value={o}>{T(o)}</option>)}
                          </select>
                          {FIELD_HINTS[tf] && (
                            <p style={{ fontSize: "0.75rem", color: "#aaa", marginTop: 4, lineHeight: 1.5, maxWidth: 380 }}>
                              {T(FIELD_HINTS[tf])}
                            </p>
                          )}
                        </div>
                      )}
                      {mode === "column" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 228 }}>
                          <select style={{ ...S.select, background: mapping[tf] ? "#fffdf7" : "#fff", maxWidth: 220 }}
                            value={mapping[tf] || ""}
                            onChange={(e) => setMapping((m2) => ({ ...m2, [tf]: e.target.value || undefined }))}>
                            <option value="">{T("— ne pas importer")}</option>
                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                          {mapping[tf] && autoMapped[tf] === mapping[tf] && (
                            <span title={T("Colonne reconnue automatiquement d'après son nom : vérifiez-la")} style={{ fontSize: "0.72rem", color: "#3a7a35", whiteSpace: "nowrap" }}>{T("✓ reconnue")}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                // ── Champs normaux ─────────────────────────────────────────
                return (
                  <div key={tf} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={S.label}>{fl(tf)}</div>
                    <select
                      style={{ ...S.select, background: mapping[tf] ? "#fffdf7" : "#fff" }}
                      value={mapping[tf] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [tf]: e.target.value || undefined }))}
                    >
                      <option value="">{T("— ne pas importer")}</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mapping[tf] && autoMapped[tf] === mapping[tf] && (
                      <span title={T("Colonne reconnue automatiquement d'après son nom : vérifiez-la")} style={{ fontSize: "0.72rem", color: "#3a7a35", whiteSpace: "nowrap" }}>{T("✓ reconnue")}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <p style={S.hint}>
              {T("Format dimensions : ")}<strong>{T("Hauteur × Largeur")}</strong>{T(" ou ")}<strong>{T("Hauteur × Largeur × Profondeur")}</strong>{T(" (en cm).")}
            </p>
          </div>

          {/* Aperçu */}
          <div style={{ ...S.box, overflowX: "auto" }}>
            <h4 style={S.sectionTitle}>{T("Aperçu — 3 premières lignes")}</h4>
            <table style={{ fontSize: "0.8rem", borderCollapse: "collapse", minWidth: 400 }}>
              <thead>
                <tr>
                  {TARGET_FIELDS.filter((tf) =>
                    tf === "artist"
                      ? (artistMode === "single" ? !!mapping["artist"] : !!(artistFirst || artistLast))
                      : !!mapping[tf]
                  ).map((tf) => (
                    <th key={tf} style={{ padding: "4px 10px", borderBottom: "1px solid #ddd", textAlign: "left", color: "#888", fontWeight: 400 }}>
                      {fl(tf)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheetRows.slice(0, 3).map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f0f0f0" }}>
                    {TARGET_FIELDS.filter((tf) =>
                      tf === "artist"
                        ? (artistMode === "single" ? !!mapping["artist"] : !!(artistFirst || artistLast))
                        : !!mapping[tf]
                    ).map((tf) => {
                      let val = "";
                      if (tf === "artist") {
                        val = artistMode === "merge"
                          ? [artistFirst ? String(r[artistFirst] || "").trim() : "",
                             artistLast  ? String(r[artistLast]  || "").trim() : ""]
                              .filter(Boolean).join(" ")
                          : String(r[mapping["artist"]] ?? "");
                      } else {
                        val = String(r[mapping[tf]] ?? "");
                      }
                      return (
                        <td key={tf} style={{ padding: "4px 10px", color: "#333", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Choix suffixe inter-onglets ─────────────────────────────── */}
          {mapping["inventory_number"] && sheetNames.length > 1 && selectedSheet && selectedSheet !== sheetNames[0] && (
            <div style={{
              border: "1px solid #e0ddd8", borderRadius: 2,
              padding: "0.75rem 1rem", background: "#fafaf8", fontSize: "0.82rem", color: "#444",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={applySheetSuffix}
                  onChange={e => setApplySheetSuffix(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  <strong>{T("Ajouter un suffixe aux numéros d'inventaire")}</strong>
                  {applySheetSuffix ? (
                    <span style={{ color: "#c9a961" }}>
                      {T(" — suffixe ")}<strong>-{selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()}</strong>{T(" activé (ex : MA-37 → MA-37-")}{selectedSheet.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase()}).
                    </span>
                  ) : (
                    <span style={{ color: "#888" }}>
                      {T(" — à cocher uniquement si les numéros d'inventaire de cet onglet sont identiques à ceux déjà importés et qu'il faut les distinguer.")}
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}

          {/* ── Avertissement doublons ─────────────────────────────────── */}
          {dupCount > 0 && mapping["inventory_number"] && (
            <div style={{
              border: "1px solid #f0c080", borderRadius: 2,
              padding: "1rem 1.25rem", background: "#fffbf0",
            }}>
              <p style={{ fontSize: "0.85rem", color: "#a06010", marginBottom: "0.75rem" }}>
                <strong>{T("{n} numéro(s) d'inventaire en doublon", { n: dupCount })}</strong>{T(" détecté(s) dans votre fichier.")}
                <br />
                <span style={{ fontSize: "0.8rem", color: "#888", marginTop: 4, display: "block" }}>
                  {T("Cela arrive quand une galerie utilise la même référence pour plusieurs œuvres d'un même lot (ex : plusieurs tirages sous PP-54).")}
                </span>
              </p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  style={S.toggleBtn(dupStrategy === "autonumber")}
                  onClick={() => setDupStrategy("autonumber")}
                >
                  {T("Auto-numéroter (PP-54 → PP-54-2, PP-54-3…)")}
                </button>
                <button
                  style={S.toggleBtn(dupStrategy === "skip")}
                  onClick={() => setDupStrategy("skip")}
                >
                  {T("Ignorer les doublons")}
                </button>
              </div>
              {dupStrategy === "autonumber" && (
                <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.5rem" }}>
                  {T("La première occurrence garde sa référence d'origine. Les suivantes reçoivent un suffixe -2, -3…")}
                </p>
              )}
              {dupStrategy === "skip" && (
                <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.5rem" }}>
                  {T("Seule la première occurrence de chaque référence sera importée.")}
                </p>
              )}
            </div>
          )}

          {/* ── Contrôle avant import (rien n'est écrit) ──────────────── */}
          {analysis && !importResult && !enrichResult && (
            <div style={{ ...S.box, borderColor: analysis.counts.withIssues ? "#f0c080" : "#e5e5e5" }}>
              <h4 style={S.sectionTitle}>{T("Contrôle avant import")}</h4>
              <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", fontSize: "0.85rem", color: "#444", marginBottom: "0.75rem" }}>
                <span><strong style={{ color: "#2a6a25" }}>{analysis.counts.toCreate}</strong>{enrichMode ? T(" fiche(s) concernée(s)") : T(" fiche(s) à créer")}</span>
                {analysis.counts.alreadyInBase > 0 && (
                  <span><strong style={{ color: "#a06010" }}>{analysis.counts.alreadyInBase}</strong>{enrichMode ? T(" déjà présente(s) en base") : T(" déjà présente(s) : ignorée(s)")}</span>
                )}
                {analysis.counts.duplicateInFile > 0 && (
                  <span><strong style={{ color: "#a06010" }}>{analysis.counts.duplicateInFile}</strong>{T(" référence(s) répétée(s) dans le fichier")}</span>
                )}
                <span><strong style={{ color: analysis.counts.withIssues ? "#a06010" : "#2a6a25" }}>{analysis.counts.withIssues}</strong>{T(" ligne(s) à vérifier")}</span>
                <span style={{ color: "#aaa" }}>{T("sur {n} ligne(s)", { n: analysis.counts.total })}</span>
              </div>

              {analysis.counts.withIssues === 0 && analysis.counts.alreadyInBase === 0 && (
                <p style={{ fontSize: "0.82rem", color: "#2a6a25" }}>{T("✓ Aucune anomalie détectée dans les colonnes associées.")}</p>
              )}

              <div style={{ overflowX: "auto" }}>
                <table style={{ fontSize: "0.78rem", borderCollapse: "collapse", minWidth: 480, width: "100%" }}>
                  <thead>
                    <tr style={{ color: "#888", textAlign: "left" }}>
                      <th style={{ padding: "4px 8px", fontWeight: 400 }}>{T("Ligne")}</th>
                      <th style={{ padding: "4px 8px", fontWeight: 400 }}>{T("Titre")}</th>
                      <th style={{ padding: "4px 8px", fontWeight: 400 }}>{T("Artiste")}</th>
                      <th style={{ padding: "4px 8px", fontWeight: 400 }}>{T("Référence")}</th>
                      <th style={{ padding: "4px 8px", fontWeight: 400 }}>{T("Résultat")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.lines
                      .filter((l) => l.issues.length > 0)
                      .concat(analysis.lines.filter((l) => l.issues.length === 0).slice(0, 5))
                      .slice(0, 12)
                      .sort((a, b) => a.line - b.line)
                      .map((l) => (
                        <tr key={l.line} style={{ borderTop: "1px solid #f0f0f0", verticalAlign: "top" }}>
                          <td style={{ padding: "4px 8px", color: "#888" }}>{l.line}</td>
                          <td style={{ padding: "4px 8px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.title || <em style={{ color: "#aaa" }}>{T("(vide)")}</em>}</td>
                          <td style={{ padding: "4px 8px", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.artist}</td>
                          <td style={{ padding: "4px 8px" }}>{l.ref}</td>
                          <td style={{ padding: "4px 8px", color: l.issues.some((x) => x.level === "warn") ? "#a06010" : "#2a6a25" }}>
                            {l.issues.length === 0 ? T("✓ prête") : l.issues.map((x) => x.text).join(" ")}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "0.5rem" }}>
                {T("Les lignes à vérifier sont affichées en premier (12 au plus). Le rapport complet est téléchargeable.")}
              </p>
              {entriesFromAnalysis(analysis, T).length > 0 && (
                <button
                  style={{ ...S.toggleBtn(false), marginTop: "0.5rem" }}
                  onClick={() => downloadReport(entriesFromAnalysis(analysis, T), "controle-import.csv")}
                >
                  {T("Télécharger le rapport de contrôle (CSV)")}
                </button>
              )}
            </div>
          )}

          {undoMsg && (
            <div style={{ border: "1px solid #d8d2c4", borderRadius: 2, padding: "0.9rem 1.25rem", background: "#faf9f6", fontSize: "0.85rem", color: "#444" }}>
              {undoMsg}
            </div>
          )}

          {/* ── Incident pendant l'import : reprise possible ───────────── */}
          {importProblem && (
            <div style={{ border: "1px solid #e0b4a8", borderRadius: 2, padding: "1rem 1.25rem", background: "#fff6f3" }}>
              <p style={{ fontSize: "0.9rem", color: "#8a3a22", marginBottom: "0.5rem" }}>
                <strong>{T("L'import n'est pas terminé.")}</strong> {importProblem.message}
              </p>
              <p style={{ fontSize: "0.82rem", color: "#555", marginBottom: "0.75rem" }}>
                {T("{imported} œuvre(s) déjà enregistrée(s) sur {total}. Ce qui est passé reste en place ; la reprise ne le recrée pas.", { imported: importProblem.imported, total: importProblem.total })}
              </p>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {importProblem.from !== null && importProblem.from !== undefined && (
                  <button
                    style={{ ...S.toggleBtn(true), padding: "6px 16px" }}
                    disabled={loading}
                    onClick={() => submit({
                      from: importProblem.from, imported: importProblem.imported, jobId: importProblem.jobId,
                      serverErrors: importProblem.serverErrors, skipped: importProblem.skipped,
                    })}
                  >
                    {loading ? T("Reprise en cours…") : T("Reprendre à la ligne {n}", { n: importProblem.from + 1 })}
                  </button>
                )}
                {importProblem.jobId && importProblem.imported > 0 && (
                  <button
                    disabled={undoBusy}
                    style={{ ...S.toggleBtn(false), color: "#991b1b", borderColor: "#e3c4c4" }}
                    onClick={() => undoImport(importProblem.jobId, importProblem.imported)}
                  >
                    {undoBusy ? T("Annulation…") : T("Annuler ce qui a été importé ({n})", { n: importProblem.imported })}
                  </button>
                )}
                <button
                  style={S.toggleBtn(false)}
                  onClick={() => downloadReport([
                    { line: importProblem.from != null ? importProblem.from + 1 : "", type: T("Incident"), detail: importProblem.message },
                    ...(importProblem.serverErrors || []).map((m) => ({ line: "", type: T("Erreur d'enregistrement"), detail: m })),
                  ], "incident-import.csv")}
                >
                  {T("Télécharger le rapport (CSV)")}
                </button>
              </div>
            </div>
          )}

          {/* ── Écran de succès ───────────────────────────────────────── */}
          {importResult && (
            <div style={{
              border: "1px solid #b5d5b0", borderRadius: 2,
              padding: "1.25rem 1.5rem", background: "#f0faf0",
            }}>
              <p style={{ fontSize: "1rem", color: "#2a6a25", marginBottom: "0.75rem" }}>
                ✓ <strong>{T("{n} œuvre(s)", { n: importResult.count })}</strong>{T(" importée(s) avec succès")}
                {importResult.sheetName ? T(" depuis l'onglet « {sheet} »", { sheet: importResult.sheetName }) : ""}.
              </p>
              {importResult.skipped > 0 && (
                <p style={{ fontSize: "0.85rem", color: "#a06010", margin: "0 0 0.5rem" }}>
                  {T("{n} ligne(s) non enregistrée(s) : numéro d'inventaire ou référence externe déjà présent dans vos œuvres.", { n: importResult.skipped })}
                </p>
              )}
              {importResult.errors?.length > 0 && (
                <div style={{ margin: "0 0 0.75rem" }}>
                  <p style={{ fontSize: "0.85rem", color: "#8a3a22", margin: "0 0 0.25rem" }}>
                    {T("{n} ligne(s) refusée(s) à l'enregistrement :", { n: importResult.errors.length })}
                  </p>
                  <ul style={{ fontSize: "0.78rem", color: "#8a3a22", margin: 0, paddingLeft: "1.1rem" }}>
                    {importResult.errors.slice(0, 5).map((m, k) => <li key={k}>{m}</li>)}
                  </ul>
                </div>
              )}
              {(importResult.skipped > 0 || importResult.errors?.length > 0) && (
                <button
                  style={{ ...S.toggleBtn(false), marginBottom: "0.75rem" }}
                  onClick={() => downloadReport([
                    ...entriesFromAnalysis(analysis, T),
                    ...(importResult.errors || []).map((m) => ({ line: "", type: T("Erreur d'enregistrement"), detail: m })),
                    ...(importResult.skipped > 0 ? [{ line: "", type: T("Ignorées"), detail: T("{n} ligne(s) déjà présentes (numéro d'inventaire ou référence externe).", { n: importResult.skipped }) }] : []),
                  ], "rapport-import.csv")}
                >
                  {T("Télécharger le rapport (CSV)")}
                </button>
              )}
              {importResult.updated > 0 && (
                <p style={{ fontSize: "0.85rem", color: "#2a6a25", margin: "0 0 0.5rem" }}>
                  {T("↻ {n} œuvre(s) déjà connue(s) par leur référence externe ont été mises à jour (aucun doublon créé).", { n: importResult.updated })}
                </p>
              )}
              {importResult.duplicatesInFile > 0 && (
                <p style={{ fontSize: "0.85rem", color: "#6b6459", margin: "0 0 0.5rem" }}>
                  {T("{n} ligne(s) ignorée(s) : même référence externe répétée dans le fichier.", { n: importResult.duplicatesInFile })}
                </p>
              )}
              {importResult.warning && (
                <p style={{ fontSize: "0.85rem", color: "#a06010", margin: "0 0 0.75rem" }}>⚠ {importResult.warning}</p>
              )}

              {/* Proposer les autres onglets si disponibles */}
              {workbook && sheetNames.filter(n => n !== importResult.sheetName).length > 0 && (
                <div style={{ marginBottom: "1rem" }}>
                  <p style={{ fontSize: "0.85rem", color: "#444", marginBottom: "0.5rem" }}>
                    {T("Votre fichier contient d'autres onglets. Voulez-vous les importer aussi ?")}
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {sheetNames.filter(n => n !== importResult.sheetName).map(name => (
                      <button key={name}
                        style={{
                          padding: "0.4rem 1rem", borderRadius: 2, fontSize: "0.85rem",
                          border: "1px solid #1a1a1a", background: "#1a1a1a",
                          color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif",
                        }}
                        onClick={() => {
                          setImportResult(null);
                          loadSheet(workbook, name);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}>
                        {T("Importer l'onglet « {name} »", { name })}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                {importResult.jobId && importResult.count > 0 && (
                  <button
                    disabled={undoBusy}
                    style={{
                      padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem",
                      border: "1px solid #e3c4c4", background: "#fff", color: "#991b1b",
                      cursor: undoBusy ? "wait" : "pointer", fontFamily: "Georgia, serif",
                    }}
                    onClick={() => undoImport(importResult.jobId, importResult.count)}
                  >
                    {undoBusy ? T("Annulation…") : T("Annuler cet import")}
                  </button>
                )}
                <button
                  style={{
                    padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem",
                    border: "1px solid #1a1a1a", background: "#fff",
                    color: "#1a1a1a", cursor: "pointer", fontFamily: "Georgia, serif",
                  }}
                  onClick={() => {
                    setImportResult(null);
                    setSheetRows([]);
                    setMapping({});
                    setWorkbook(null);
                    setSheetNames([]);
                    setSelectedSheet(null);
                    setFixedValues({});
                    setPresetMode({});
                    setDupCount(0);
                  }}>
                  {T("Nouvel import")}
                </button>
                <a href={`/collections/${collectionId}`}
                  style={{
                    padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem",
                    border: "1px solid #1a1a1a", background: "#1a1a1a",
                    color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif",
                    textDecoration: "none", display: "inline-block",
                  }}>
                  {T("Voir ma collection →")}
                </a>
              </div>
            </div>
          )}

          {/* Résultat enrichissement */}
          {enrichResult && (
            <div style={{ border: "1px solid #b5d5b0", borderRadius: 4, padding: "1rem 1.5rem", background: "#f0faf0", fontSize: "0.9rem" }}>
              <strong style={{ color: "#3a7a35" }}>{T("Enrichissement terminé")}</strong>
              <div style={{ marginTop: 8, color: "#444", lineHeight: 1.8 }}>
                {T("✅ {n} fiche(s) mise(s) à jour", { n: enrichResult.updated })}<br />
                {enrichResult.notFound > 0 && <>{T("⚠️ {n} numéro(s) d'inventaire non trouvé(s) en base", { n: enrichResult.notFound })}<br /></>}
                {enrichResult.skipped > 0 && <>{T("— {n} ligne(s) ignorée(s) (sans données)", { n: enrichResult.skipped })}<br /></>}
              </div>
            </div>
          )}

          {!importResult && !enrichResult && (
          <button
            className="btn-artsy btn-primary-artsy"
            onClick={() => (enrichMode ? handleEnrich() : submit())}
            disabled={loading}
            style={{ minWidth: 200 }}
          >
            {loading
              ? (enrichMode ? T("Enrichissement en cours…") : T("Import en cours…"))
              : enrichMode
                ? T("Enrichir {n} fiche(s)", { n: sheetRows.length })
                : T("Importer {n} œuvre(s)", { n: sheetRows.length })
            }
          </button>
          )}
        </>
      )}

      {/* ── Étape suivante : images et documents, rattachés par numéro d'inventaire ── */}
      {collectionId && <ImportFilesByInventory collectionId={collectionId} />}
    </div>
  );
}